import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { knownErrorHandling } from '../../services/knownErrorHandling'
import { ApiErrorResponse } from '../../services/isApiError'
import { showToast } from '../toastSlice'

const API_URL = process.env.REACT_APP_API_URL
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/api/v1'

const baseQuery = fetchBaseQuery({
	baseUrl: `${API_URL}${API_PREFIX}`,
	prepareHeaders: (headers, { endpoint }) => {
		const token = localStorage.getItem('access_token')
		if (token) {
			headers.set('authorization', `Bearer ${token}`)
		}
		headers.set('Accept', 'application/json')
		if (endpoint !== 'uploadInvoiceFile') {
			headers.set('Content-Type', 'application/json')
		}
		return headers
	},
})

export const transformErrorToApiResponse = (
	error: FetchBaseQueryError | unknown
): ApiErrorResponse | null => {
	if (
		typeof error === 'object' &&
		error !== null &&
		'status' in error &&
		('data' in error || typeof (error as any).status === 'number')
	) {
		const fetchError = error as { status: number | string; data?: unknown }
		const status =
			typeof fetchError.status === 'number' ? fetchError.status : 500
		if (status === 401) return null

		if (
			'data' in fetchError &&
			typeof fetchError.data === 'object' &&
			fetchError.data !== null
		) {
			const data =
				'message' in fetchError.data || 'errors' in fetchError.data
					? (fetchError.data as ApiErrorResponse['data'])
					: { message: 'Сталася помилка' }

			return {
				status,
				data,
			}
		}

		return {
			status,
			data: { message: 'Сталася помилка' },
		}
	}

	return null
}

const transformError = (error: FetchBaseQueryError): ApiErrorResponse | null => {
	return transformErrorToApiResponse(error)
}

const baseQueryWithReauth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	const result = await baseQuery(args, api, extraOptions)

	if (result.error) {
		const errorStatus =
			typeof result.error.status === 'number' ? result.error.status : null

		if (errorStatus === 401) {
			localStorage.removeItem('access_token')
			return result
		}

		const apiError = transformError(result.error)
		if (apiError) {
			if (
				apiError.data.message &&
				apiError.data.message.includes('User does not have the right permissions.')
			) {
				api.dispatch(showToast('У вас немає прав для цієї дії'))
				return result
			}

			if (apiError.status === 422 && apiError.data.message) {
				api.dispatch(showToast(apiError.data.message))
				return result
			}

			const handledError = knownErrorHandling(apiError)
			if (handledError && handledError.message) {
				api.dispatch(showToast(handledError.message))
			} else if (apiError.status === 500) {
				api.dispatch(showToast('Помилка серверу'))
			} else if (apiError.data.message) {
				api.dispatch(showToast(apiError.data.message))
			} else {
				api.dispatch(showToast('Сталася помилка, спробуйте пізніше'))
			}
		}
	}

	return result
}

export const baseApi = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	tagTypes: ['Invoice', 'Option', 'Rule', 'User'],
	endpoints: () => ({}),
})

